/**
 * FluentUI Theme Configuration
 *
 * Custom FluentUI themes that integrate with CSS variables from variables.css.
 * Single source of truth: variables.css defines the values, this file maps them to FluentUI.
 *
 * Usage:
 * ```tsx
 * import { FluentProvider } from '@fluentui/react-components';
 * import { customLightTheme, customDarkTheme } from './fluentTheme';
 *
 * // For main app (light theme)
 * <FluentProvider theme={customLightTheme}>
 *   <App />
 * </FluentProvider>
 *
 * // For navigation or dark sections
 * <FluentProvider theme={customDarkTheme}>
 *   <NavigationBar />
 * </FluentProvider>
 * ```
 */

import type { Theme, BrandVariants } from "@fluentui/react-components";
import { createDarkTheme, createLightTheme } from "@fluentui/react-components";
import { brand } from "@brand";

// =============================================================================
// BRAND PALETTE
// Per brand: code/brands/<brand>/fluent.ts
// =============================================================================

export const brandColors: BrandVariants = brand.fluent.brandColors;

// =============================================================================
// SHARED CUSTOMIZATIONS
// Typography, spacing, and animations matching variables.css
// =============================================================================

const sharedCustomizations = {
    // Font family
    fontFamilyMonospace: '"IBM Plex Mono", Consolas, monospace',

    // Font sizes
    fontSizeBase100: "11.24px",
    fontSizeBase200: "12.64px",
    fontSizeBase300: "14.22px",
    fontSizeBase400: "16px",
    fontSizeBase500: "20.25px",
    fontSizeBase600: "25.63px",

    // Font weights
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightSemibold: 600,
    fontWeightBold: 700,

    // Line heights
    lineHeightBase100: "14px",
    lineHeightBase200: "16px",
    lineHeightBase300: "20px",
    lineHeightBase400: "22px",
    lineHeightBase500: "28px",
    lineHeightBase600: "32px",

    // Border radius
    borderRadiusNone: "0px",
    borderRadiusSmall: "2px",
    borderRadiusMedium: "4px",
    borderRadiusLarge: "6px",
    borderRadiusXLarge: "8px",
    borderRadiusCircular: "10000px",

    // Spacing - Horizontal
    spacingHorizontalNone: "0px",
    spacingHorizontalXXS: "2px",
    spacingHorizontalXS: "4px",
    spacingHorizontalSNudge: "6px",
    spacingHorizontalS: "8px",
    spacingHorizontalMNudge: "10px",
    spacingHorizontalM: "12px",
    spacingHorizontalL: "16px",
    spacingHorizontalXL: "20px",
    spacingHorizontalXXL: "24px",
    spacingHorizontalXXXL: "32px",

    // Spacing - Vertical
    spacingVerticalNone: "0px",
    spacingVerticalXXS: "2px",
    spacingVerticalXS: "4px",
    spacingVerticalSNudge: "6px",
    spacingVerticalS: "8px",
    spacingVerticalMNudge: "10px",
    spacingVerticalM: "12px",
    spacingVerticalL: "16px",
    spacingVerticalXL: "20px",
    spacingVerticalXXL: "24px",
    spacingVerticalXXXL: "32px",

    // Animation durations
    durationUltraFast: "50ms",
    durationFaster: "100ms",
    durationFast: "150ms",
    durationNormal: "200ms",
    durationGentle: "250ms",
    durationSlow: "300ms",
    durationSlower: "400ms",
    durationUltraSlow: "500ms",

    // Easing curves
    curveAccelerateMax: "cubic-bezier(1,0,1,1)",
    curveAccelerateMid: "cubic-bezier(0.7,0,1,0.5)",
    curveAccelerateMin: "cubic-bezier(0.8,0,1,1)",
    curveDecelerateMax: "cubic-bezier(0,0,0,1)",
    curveDecelerateMid: "cubic-bezier(0.1,0.9,0.2,1)",
    curveDecelerateMin: "cubic-bezier(0.33,0,0.1,1)",
    curveEasyEaseMax: "cubic-bezier(0.8,0,0.1,1)",
    curveEasyEase: "cubic-bezier(0.33,0,0.67,1)",
    curveLinear: "cubic-bezier(0,0,1,1)",

    // Brand values (code/brands/<brand>/fluent.ts)
    ...brand.fluent.fonts
};

// =============================================================================
// LIGHT THEME
// =============================================================================

const baseLightTheme = createLightTheme(brandColors);

export const customLightTheme: Theme = {
    ...baseLightTheme,
    ...sharedCustomizations,

    colorNeutralBackground1: "#ffffff", // --neutral-White
    colorSubtleBackground: "transparent",

    // Brand values: backgrounds, foregrounds, brand, subtle, strokes, shadows (code/brands/<brand>/fluent.ts)
    ...brand.fluent.light
};

// =============================================================================
// DARK THEME
// =============================================================================

const baseDarkTheme = createDarkTheme(brandColors);

export const customDarkTheme: Theme = {
    ...baseDarkTheme,
    ...sharedCustomizations,

    colorSubtleBackground: "transparent",
    colorNeutralStroke1: "rgba(255, 255, 255, 0.1)",

    // Brand values: backgrounds, foregrounds, brand, subtle, strokes, shadows (code/brands/<brand>/fluent.ts)
    ...brand.fluent.dark
};

// =============================================================================
// NAVIGATION TOKENS
// CSS variable references for navigation components
// =============================================================================

export const navTokens = {
    widthCollapsed: "var(--nav-width-collapsed)",
    widthExpanded: "var(--nav-width-expanded)",
    background: "var(--nav-background)",
    foreground: "var(--nav-foreground)",
    foregroundMuted: "var(--color-text-muted)",
    foregroundHover: "var(--theme-Primary)",
    backgroundHover: "var(--button-subtle-secondary-inverse-background-hover)",
    backgroundPressed: "var(--button-subtle-secondary-inverse-background-pressed)",
    backgroundSelected: "var(--button-subtle-primary-inverse-background-hover)",
    borderSubtle: "var(--color-border-subtle)",
    radiusMedium: "var(--stroke-Radius-Medium)",
    radiusLarge: "var(--stroke-Radius-XLarge)",
    shadow: "var(--shadow-navigation)",
    transition: "var(--transition-default)"
};

// =============================================================================
// THEME SELECTION HELPER
// =============================================================================

/**
 * Returns the appropriate FluentUI theme based on dark mode state.
 *
 * @example
 * ```tsx
 * import { useTheme } from '@/theme';
 * import { getFluentTheme } from '@/theme/fluentTheme';
 *
 * function App() {
 *   const { isDarkMode } = useTheme();
 *   return (
 *     <FluentProvider theme={getFluentTheme(isDarkMode)}>
 *       <MyApp />
 *     </FluentProvider>
 *   );
 * }
 * ```
 */
export function getFluentTheme(isDarkMode: boolean): Theme {
    return isDarkMode ? customDarkTheme : customLightTheme;
}

// =============================================================================
// EXPORTS
// =============================================================================

export default customLightTheme;
